import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LOGO_URL, COMPANY } from "@/lib/constants";
import FreightSummary from "@/components/FreightSummary";

const Addr = ({ label, a }) => (
  <div>
    <div className="mono-label">{label}</div>
    <div className="mt-1 text-sm">
      {a?.name && <div className="font-semibold">{a.name}</div>}
      <div>{a?.street}</div>
      <div>{a?.city}, {a?.state} {a?.zip}</div>
      {a?.contact_name && <div className="text-xs mt-1 text-zinc-600">Contact: {a.contact_name}{a.contact_phone && ` · ${a.contact_phone}`}</div>}
    </div>
  </div>
);

export default function BolView() {
  const { id } = useParams();
  const [job, setJob] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/jobs/${id}`);
      if (!data.bol_number) {
        // Generate on the fly if missing (admin only endpoint; ignore errors for customers)
        try {
          const { data: withBol } = await api.post(`/jobs/${id}/bol`);
          setJob(withBol); return;
        } catch { setJob(data); return; }
      }
      setJob(data);
    })();
  }, [id]);

  if (!job) return <div className="p-10 mono-label">Loading BOL...</div>;

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="no-print bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between">
        <Link to="/admin" className="mono-label">&larr; BACK</Link>
        <Button onClick={() => window.print()} className="sharp bg-zinc-950 hover:bg-zinc-800" data-testid="print-bol">
          <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
        </Button>
      </div>
      <div className="max-w-4xl mx-auto bg-white my-8 shadow-sm printable p-12 border border-zinc-950">
        <div className="flex items-start justify-between border-b-2 border-zinc-950 pb-6">
          <div className="flex items-start gap-4">
            <img src={LOGO_URL} alt="Apex" className="h-16 w-16 object-contain" />
            <div>
              <div className="text-2xl font-bold tracking-tight">{COMPANY.name}</div>
              <div className="text-xs text-zinc-600 mt-1 leading-relaxed">
                {COMPANY.address}<br />
                {COMPANY.phone} · {COMPANY.email}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="mono-label">BILL OF LADING</div>
            <div className="font-mono text-2xl font-bold mt-1">{job.bol_number || "PENDING"}</div>
            <div className="mono-label mt-2">Job {job.job_number}</div>
            {job.po_number && <div className="mono-label">PO {job.po_number}</div>}
            {job.reference && <div className="mono-label">REF {job.reference}</div>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-8">
          <Addr label="SHIPPER (PICKUP)" a={job.pickup} />
          <Addr label="CONSIGNEE (DELIVERY)" a={job.delivery} />
        </div>

        <FreightSummary doc={job} />

        <div className="mt-6 border border-zinc-200 p-4 bg-zinc-50">
          <div className="mono-label">CARRIER INFORMATION</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-2 text-sm">
            <div className="col-span-2 md:col-span-1">
              <span className="mono-label block">Carrier</span>
              <span className="font-semibold" data-testid="bol-carrier-name">{job.carrier_name || "—"}</span>
              {job.mc_number && <span className="ml-2 font-mono text-xs text-zinc-600">MC {job.mc_number}</span>}
            </div>
            <div><span className="mono-label block">Driver</span>{job.driver_name || "—"}</div>
            <div><span className="mono-label block">Truck</span><span className="font-mono">{job.truck_number || "—"}</span></div>
            <div><span className="mono-label block">Trailer</span><span className="font-mono">{job.trailer_number || "—"}</span></div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-8">
          <div>
            <div className="mono-label mb-8">SHIPPER SIGNATURE</div>
            <div className="border-b border-zinc-950 h-8" />
            <div className="text-xs mt-1 text-zinc-500">Date &nbsp; / &nbsp; Print name</div>
          </div>
          <div>
            <div className="mono-label mb-8">CONSIGNEE SIGNATURE</div>
            <div className="border-b border-zinc-950 h-8" />
            <div className="text-xs mt-1 text-zinc-500">Date &nbsp; / &nbsp; Print name</div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-zinc-200 text-xs text-zinc-500 text-center mono-label">
          Received in good order except as noted. Subject to Apex Supply Chain Solutions Terms &amp; Conditions.
        </div>
      </div>
    </div>
  );
}
